import React, { useRef } from 'react'
import CalendarModal from './CalendarModal'

function DateTimeSelection({ 
  workers, 
  selectedWorker, 
  onWorkerSelect, 
  allDates, 
  currentDateIndex, 
  onScrollDatesLeft, 
  onScrollDatesRight, 
  getCurrentMonthYear, 
  onShowCalendar, 
  showCalendar, 
  onCloseCalendar, 
  onCalendarDateSelect, 
  selectedDate, 
  onDateSelect, 
  availableTimes, 
  selectedTime, 
  onTimeSelect,
  availabilityData,
  selectedService
}) {
  const dateScrollRef = useRef(null)

  // Helper function to convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }
    return `${hours.padStart(2, '0')}:${minutes}`;
  }

  // Calculate which time slots this appointment would occupy
  const getOccupiedSlots = (startTime) => {
    if (!selectedService || !startTime || !selectedDate) return [];
    
    const startDateTime = new Date(`${selectedDate}T${convertTo24Hour(startTime)}:00`);
    const endDateTime = new Date(startDateTime.getTime() + (selectedService.duration * 60000));
    
    const occupiedSlots = [];
    availableTimes.forEach(timeSlot => {
      const slotTime = typeof timeSlot === 'string' ? timeSlot : timeSlot.time;
      const slotDateTime = new Date(`${selectedDate}T${convertTo24Hour(slotTime)}:00`);
      
      // Check if this slot falls within the appointment duration
      if (slotDateTime >= startDateTime && slotDateTime < endDateTime) {
        occupiedSlots.push(slotTime);
      }
    });
    
    return occupiedSlots;
  }

  // Check if appointment would extend past business/worker hours
  const checkTimeExtension = (startTime) => {
    if (!selectedService || !startTime || !selectedDate) return true;
    
    const startDateTime = new Date(`${selectedDate}T${convertTo24Hour(startTime)}:00`);
    const endDateTime = new Date(startDateTime.getTime() + (selectedService.duration * 60000));
    
    // Get availability data for selected date
    const dayIntervals = availabilityData[selectedDate] || [];
    if (dayIntervals.length === 0) return false;
    
    // Find the latest available end time
    let latestEndTime = null;
    for (const interval of dayIntervals) {
      const intervalEndTime = new Date(`${selectedDate}T${interval.end_time}:00`);
      if (!latestEndTime || intervalEndTime > latestEndTime) {
        latestEndTime = intervalEndTime;
      }
    }
    
    // Check if appointment would end after business/worker hours
    if (latestEndTime && endDateTime > latestEndTime) {
      return false;
    }
    
    return true;
  }

  // Check if a time selection is valid (all required slots are available and within hours)
  const isValidTimeSelection = (startTime) => {
    if (!selectedService || !startTime) return true;
    
    // Check if appointment extends past hours
    if (!checkTimeExtension(startTime)) return false;
    
    const occupiedSlots = getOccupiedSlots(startTime);
    
    // Check if all occupied slots are available
    return occupiedSlots.every(slotTime => {
      const timeSlot = availableTimes.find(t => {
        const time = typeof t === 'string' ? t : t.time;
        return time === slotTime;
      });
      
      if (!timeSlot) return false;
      const isAvailable = typeof timeSlot === 'string' ? true : timeSlot.available;
      return isAvailable;
    });
  }

  const occupiedSlots = selectedTime ? getOccupiedSlots(selectedTime) : []

  const scrollLeft = () => {
    if (dateScrollRef.current) {
      dateScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (dateScrollRef.current) {
      dateScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Show more dates for scrolling (e.g., 30 days instead of 7)
  const visibleDates = allDates.slice(0, 30)

  return (
    <div className="booking-step">
      <h2>Select Date & Time</h2>
      
      <div className="date-selection">
        <div className="date-header">
          <h3>{getCurrentMonthYear()}</h3>
          <button className="calendar-toggle-btn" onClick={onShowCalendar}>
            📅 Calendar
          </button>
        </div>
        
        <div className="date-navigation">
          <button className="scroll-button left" onClick={scrollLeft}>
            &#8249;
          </button>
          <div className="date-scroll-container" ref={dateScrollRef}>
            <div className="dates-grid">
              {visibleDates.map((date) => {
                const dateKey = date.toDateString()
                const isoDateString = date.toISOString().split('T')[0] // Convert to YYYY-MM-DD format
                
                // Check if this date has any intervals
                const dayIntervals = availabilityData[isoDateString] || []
                
                // Determine the status of this date
                let dateStatus = null;
                let isDateDisabled = false;
                
                if (dayIntervals.length === 0) {
                  // No intervals at all - business is closed
                  dateStatus = 'Closed';
                  isDateDisabled = true;
                } else {
                  // Check if it's today and past business hours
                  const today = new Date();
                  const isToday = isoDateString === today.toISOString().split('T')[0];
                  
                  if (isToday) {
                    // Find the latest business hour (regardless of availability)
                    const currentTime = today;
                    let latestBusinessTime = null;
                    
                    for (const interval of dayIntervals) {
                      const intervalEndTime = new Date(`${isoDateString}T${interval.end_time}:00`);
                      if (!latestBusinessTime || intervalEndTime > latestBusinessTime) {
                        latestBusinessTime = intervalEndTime;
                      }
                    }
                    
                    // If current time is past the latest business time, show closed
                    if (latestBusinessTime && currentTime > latestBusinessTime) {
                      dateStatus = 'Closed';
                      isDateDisabled = true;
                    }
                  }
                  
                  // If not closed due to time, check if fully booked
                  if (!dateStatus) {
                    const hasAvailableSlots = dayIntervals.some(interval => interval.available);
                    if (!hasAvailableSlots) {
                      dateStatus = 'Full';
                      isDateDisabled = true;
                    }
                  }
                }
                
                return (
                  <button
                    key={dateKey}
                    className={`date-btn ${selectedDate === isoDateString ? 'selected' : ''} ${isDateDisabled ? 'disabled' : ''}`}
                    onClick={() => isDateDisabled ? null : onDateSelect(isoDateString)}
                    disabled={isDateDisabled}
                  >
                    <div className="date-day">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className="date-number">{date.getDate()}</div>
                    {dateStatus && <div className="closed-indicator">{dateStatus}</div>}
                  </button>
                )
              })}
            </div>
          </div>
          <button className="scroll-button right" onClick={scrollRight}>
            &#8250;
          </button>
        </div>
      </div>

      {selectedDate && (
        <div className="time-selection">
          <h3>Select Time</h3>
          {selectedService && (
            <p style={{ textAlign: 'center', color: '#5f6368', fontSize: '14px', marginBottom: '1rem' }}>
              Service duration: {selectedService.duration} minutes
            </p>
          )}
          <div className="times-grid">
            {availableTimes.map((timeSlot) => {
              // Handle both old format (string) and new format (object) for backward compatibility
              const timeDisplay = typeof timeSlot === 'string' ? timeSlot : timeSlot.time
              const isAvailable = typeof timeSlot === 'string' ? true : timeSlot.available
              const timeValue = typeof timeSlot === 'string' ? timeSlot : timeSlot.time
              
              // Check if this slot is part of the selected appointment duration
              const isOccupied = occupiedSlots.includes(timeValue)
              const isStartTime = selectedTime === timeValue
              
              // Check if selecting this time would be valid
              const wouldBeValid = isAvailable && isValidTimeSelection(timeValue)
              
              // Show as occupied if it's part of the duration (even if unavailable)
              const showAsOccupied = isOccupied && !isStartTime && selectedTime
              
              return (
                <button
                  key={timeDisplay}
                  className={`time-btn ${isStartTime ? 'selected' : ''} ${showAsOccupied ? 'occupied' : ''} ${!isAvailable || !wouldBeValid ? 'disabled' : ''}`}
                  onClick={() => (isAvailable && wouldBeValid) ? onTimeSelect(timeValue, selectedDate) : null}
                  disabled={!isAvailable || !wouldBeValid}
                  title={showAsOccupied ? 'Part of selected appointment' : (!wouldBeValid && isAvailable ? 'Service would extend past closing hours or into unavailable time' : '')}
                >
                  {timeDisplay}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <CalendarModal 
        showCalendar={showCalendar}
        onClose={onCloseCalendar}
        onDateSelect={onCalendarDateSelect}
        selectedDate={selectedDate}
      />
    </div>
  )
}

export default DateTimeSelection